import { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Seo from '@/components/Seo';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getProviderCategories, getProviders } from '@/lib/api';
import {
  Compass, Sofa, PencilRuler, Building2, Sun, HardHat, Hammer, CookingPot, Cpu, Camera, Droplets,
  Paintbrush, Bug, Zap, Wrench, Snowflake, Sparkles, Truck, Scale, Landmark, BadgeIndianRupee,
  Search, ArrowLeft, ArrowRight, Phone, Globe, ShieldCheck, MapPin, Store, Info,
  Layers, SquareStack, AppWindow,
} from 'lucide-react';

const ICONS = { Compass, Sofa, PencilRuler, Building2, Sun, HardHat, Hammer, CookingPot, Cpu, Camera, Droplets, Paintbrush, Bug, Zap, Wrench, Snowflake, Sparkles, Truck, Scale, Landmark, BadgeIndianRupee, Layers, SquareStack, AppWindow };

const WhatsAppIcon = (props) => (<svg viewBox="0 0 24 24" fill="currentColor" {...props}><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>);

const ProviderCard = ({ p }) => {
  const telHref = p.phone ? `tel:${p.phone.replace(/\s/g, '')}` : null;
  const waNum = (p.whatsapp || '').replace(/\D/g, '');
  const waHref = waNum ? `https://wa.me/${waNum.length === 10 ? '91' + waNum : waNum}?text=${encodeURIComponent(`Hi, I found you on Bharat Estate and would like to know more about your ${p.category_label} services.`)}` : null;
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col hover:shadow-lg transition-shadow" data-testid={`provider-card-${p.provider_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-bold text-[#0F172A] leading-snug">{p.name}</h3>
          <p className="text-xs text-slate-500 mt-0.5">{p.category_label}</p>
        </div>
        {p.verified && <span className="inline-flex items-center gap-1 text-[11px] font-bold text-[#1D4ED8] bg-[#EFF6FF] border border-[#BFDBFE] px-2 py-0.5 rounded-md flex-shrink-0"><ShieldCheck className="h-3 w-3" /> Verified</span>}
      </div>
      {(p.city || p.service_area) && <p className="text-xs text-slate-500 mt-2 inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {p.service_area || p.city}</p>}
      {p.description && <p className="text-sm text-slate-600 mt-2 line-clamp-3 flex-1">{p.description}</p>}
      <div className="mt-4 flex flex-wrap gap-2">
        {telHref && <a href={telHref}><Button size="sm" variant="outline" className="rounded-full h-9" data-testid={`provider-call-${p.provider_id}`}><Phone className="h-3.5 w-3.5 mr-1.5" /> Call</Button></a>}
        {waHref && <a href={waHref} target="_blank" rel="noopener noreferrer"><Button size="sm" className="rounded-full h-9 bg-[#25D366] hover:bg-[#128C7E] text-white" data-testid={`provider-wa-${p.provider_id}`}><WhatsAppIcon className="h-3.5 w-3.5 mr-1.5" /> WhatsApp</Button></a>}
        {p.website && <a href={p.website} target="_blank" rel="noopener noreferrer"><Button size="sm" variant="outline" className="rounded-full h-9"><Globe className="h-3.5 w-3.5 mr-1.5" /> Website</Button></a>}
      </div>
    </div>
  );
};

const ProfessionalsPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [city, setCity] = useState('');

  const activeCat = useMemo(() => categories.find((c) => c.slug === slug), [categories, slug]);

  useEffect(() => { getProviderCategories().then(setCategories).catch(() => {}); }, []);

  useEffect(() => {
    if (!slug) { setLoading(false); return; }
    setLoading(true);
    getProviders({ category: slug, q, city }).then(setProviders).catch(() => setProviders([])).finally(() => setLoading(false));
  }, [slug, q, city]);

  // ---------- CATEGORY PAGE ----------
  if (slug) {
    const Icon = ICONS[activeCat?.icon] || Store;
    return (
      <div className="min-h-screen bg-[#F9F9F7] pb-20">
        <Seo title={`${activeCat?.label || 'Professionals'} in India — Bharat Estate`} description={activeCat?.description || 'Find verified professional service providers on Bharat Estate.'} path={`/professionals/${slug}`} />
        <div className="bg-[#0F172A] text-white">
          <div className="container-luxury py-10">
            <Link to="/professionals" className="inline-flex items-center gap-1.5 text-white/70 hover:text-white text-sm mb-4" data-testid="pros-back-link"><ArrowLeft className="h-4 w-4" /> All Professional Services</Link>
            <div className="flex items-center gap-4">
              <span className="h-14 w-14 rounded-2xl bg-white/10 flex items-center justify-center"><Icon className="h-7 w-7 text-[#D4AF37]" /></span>
              <div><h1 className="text-2xl md:text-4xl font-extrabold">{activeCat?.label || 'Professionals'}</h1><p className="text-white/70 mt-1">{activeCat?.description}</p></div>
            </div>
          </div>
        </div>

        <div className="container-luxury py-8">
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or keyword" className="pl-10 rounded-full h-11" data-testid="pros-search-input" />
            </div>
            <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="City / area" className="rounded-full h-11 sm:w-56" data-testid="pros-city-input" />
          </div>

          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">{[...Array(6)].map((_, i) => <div key={i} className="h-44 bg-white border border-slate-200 rounded-2xl animate-pulse" />)}</div>
          ) : providers.length === 0 ? (
            <div className="text-center py-16 bg-white border border-slate-200 rounded-2xl" data-testid="pros-empty">
              <Info className="h-8 w-8 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-600 font-medium">No verified providers listed here yet.</p>
              <p className="text-sm text-slate-500 mt-1">We only publish real, verified business contacts. Check back soon or <a href="mailto:contact@bharat.estate" className="text-[#0F172A] underline font-semibold">suggest a business</a>.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="pros-provider-grid">
              {providers.map((p) => <ProviderCard key={p.provider_id} p={p} />)}
            </div>
          )}

          <p className="text-xs text-slate-400 mt-8 max-w-3xl">Contact details are sourced from official business websites / public business listings. Bharat Estate is not responsible for third-party services; please verify credentials before engaging.</p>
        </div>
      </div>
    );
  }

  // ---------- HUB PAGE ----------
  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-20">
      <Seo title="Professional Services Directory — Bharat Estate" description="Find verified Vastu experts, interior designers, architects, solar, waterproofing, pest control, painters, packers & movers and more professional services for your home." path="/professionals" />
      <div className="bg-[#0F172A] text-white">
        <div className="container-luxury py-14 text-center">
          <span className="inline-flex items-center gap-2 text-[#D4AF37] text-sm font-semibold mb-3"><Store className="h-4 w-4" /> Professional Services Directory</span>
          <h1 className="text-3xl md:text-5xl font-extrabold">Trusted professionals for your home</h1>
          <p className="text-white/70 mt-3 max-w-2xl mx-auto">Verified businesses across {categories.length || 21} categories — from Vastu and interiors to solar, waterproofing and relocation.</p>
        </div>
      </div>

      <div className="container-luxury py-10">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="pros-category-grid">
          {categories.map((c) => {
            const Icon = ICONS[c.icon] || Store;
            return (
              <Link key={c.slug} to={`/professionals/${c.slug}`} className="group bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-xl hover:-translate-y-0.5 transition-all flex items-start gap-4" data-testid={`pros-category-${c.slug}`}>
                <span className="h-12 w-12 rounded-xl bg-[#FBFAF6] border border-slate-100 flex items-center justify-center flex-shrink-0"><Icon className="h-6 w-6 text-[#0F172A]" /></span>
                <div className="min-w-0">
                  <h3 className="font-bold text-[#0F172A] group-hover:text-[#B5952F] flex items-center gap-2">{c.label} <ArrowRight className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 transition-opacity" /></h3>
                  <p className="text-sm text-slate-500 mt-1">{c.description}</p>
                  {c.count > 0 && <p className="text-xs text-slate-400 mt-2">{c.count} listed</p>}
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ProfessionalsPage;
